import React from "react";
import { theme } from "../../../../Themes/Themes";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import EditIcon from "@mui/icons-material/Edit";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Tooltip } from "@mui/material";
import useStore from "../../../../Store/Store";
import { startRecordingActions } from "../../../../Store/UndoManager";
import { INITIAL_MODIFIED_SIMULATION_STATE } from "./SimulationsTab";

interface SimulationCardProps {
  simulation: Simulation;
  simulationKey: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * Card component for displaying a single simulation in the simulations list.
 * Similar to UsecaseItem, shows truncated description when not selected,
 * and full details when selected.
 */
const SimulationCard: React.FC<SimulationCardProps> = ({
  simulation,
  simulationKey,
  selected,
  onClick,
  disabled = false,
}) => {
  const {
    simulations,
    setCurrentUnsavedSimulationObject,
    setIsEditingSimulations,
  } = useStore((state) => ({
    simulations: state.simulationsState.simulations,
    setCurrentUnsavedSimulationObject: state.setCurrentUnsavedSimulationObject,
    setIsEditingSimulations: state.setIsEditingSimulations,
  }));

  const handleEdit = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    // Use the same logic as in SimulationsControls handleEdit
    await startRecordingActions("SIMULATION_MODIFIED");
    await setCurrentUnsavedSimulationObject(
      simulation || INITIAL_MODIFIED_SIMULATION_STATE
    );
    await setIsEditingSimulations("SimSteps");
  };

  const wrapperStyle: React.CSSProperties = {
    height: "fit-content",
    minHeight: "fit-content",
    overflow: "hidden",
    transition: "height 0.2s ease",
    padding: "0px 0px 0px 0px",
  };

  return (
    <div
      className={`SourceDocFile linked ${selected ? "selectedFile" : ""}`}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? "default" : "pointer",
        borderRadius: "15px",
        padding: "8px 15px",
        width: "92%",
        minHeight: "fit-content",
        transition: "all 0.2s ease",
        display: "block",
        opacity: disabled ? 0.7 : 1,
        position: "relative",
      }}
      data-testid={`simulation-card-${simulationKey}`}
    >
      <div className="SourceDocFileInternalWrapper" style={wrapperStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {selected ? (
            <KeyboardArrowDown
              style={{
                width: "20px",
                height: "20px",
              }}
            />
          ) : (
            <KeyboardArrowRight
              style={{
                width: "20px",
                height: "20px",
              }}
            />
          )}
          {simulation.isAIGenerated ? (
            <AccountTreeRoundedIcon
              style={{
                width: "18px",
                height: "18px",
                color: "inherit", // Use same color as title
              }}
            />
          ) : (
            <MovieRoundedIcon
              style={{
                width: "18px",
                height: "18px",
              }}
            />
          )}
          <span
            className="SourceDocFileInternalText"
            style={{
              fontWeight: 600,
              flex: 1,
            }}
          >
            {simulation.name}
          </span>

          {/* Step count */}
          <span
            style={{
              color: theme.custom.darkerGrey,
              fontSize: "0.85em",
              fontStyle: "italic",
              marginLeft: simulation.isAIGenerated ? "0" : "auto",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {simulation.isAIGenerated && simulation.simSteps.length === 0 ? (
              <Tooltip title="Click to generate simulation steps">
                <AutoAwesomeIcon
                  style={{
                    width: "16px",
                    height: "16px",
                    color: theme.custom.lighterPink,
                  }}
                />
              </Tooltip>
            ) : (
              `${simulation.simSteps.length} step${
                simulation.simSteps.length !== 1 ? "s" : ""
              }`
            )}
          </span>

          {/* Edit pencil button - only show when selected */}
          {selected && (
            <div
              onClick={handleEdit}
              style={{
                marginLeft: "10px",
                padding: "4px",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <EditIcon
                style={{
                  width: "16px",
                  height: "16px",
                  color: theme.custom.lighterPink,
                }}
              />
            </div>
          )}
        </div>

        {!selected && simulation.isAIGenerated && simulation.keywords && (
          <div
            className="SourceDocFileInternalTextSub"
            style={{
              color: theme.custom.darkerGrey,
              marginTop: 4,
              marginLeft: "43px", // Align with title text (20px icon + 5px gap + 18px icon + 5px gap)
              fontSize: "0.6em",
              fontStyle: "italic",
            }}
          >
            Keywords: {simulation.keywords}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulationCard;
